/**
 * One time model for every Studio group tab: the host types the trip's local
 * wall clock, and Studio pins that to the trip's own IANA zone before it leaves
 * the browser.
 *
 * Why this exists: a `datetime-local` input yields a naive "2026-08-15T17:30"
 * with no offset. Sending that raw makes the API bind it with the server
 * process's zone (UTC on Cloud Run); running it through `new Date(local)` binds
 * it with the *browser's* zone. Both are wrong, they disagree with each other,
 * and neither is the trip's zone — a host in New York and a host in Lisbon
 * would store two different instants from the same keystrokes. Everything here
 * converts explicitly against a named zone so the stored instant depends only
 * on the trip, never on who typed it or where the API happens to run.
 */

const ISO_LOCAL = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Verified-usable zone id, memoized — Intl throws on an unknown id. */
const zoneCache = new Map<string, boolean>();

function isUsableZone(timeZone: string): boolean {
  const cached = zoneCache.get(timeZone);
  if (cached !== undefined) return cached;
  let usable = false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    usable = true;
  } catch {
    usable = false;
  }
  zoneCache.set(timeZone, usable);
  return usable;
}

/**
 * True when Intl knows the zone id. Exported so a caller can refuse a bad id
 * rather than have the helpers below quietly read it in the browser's zone.
 */
export function isUsableTimeZone(timeZone: string): boolean {
  return isUsableZone((timeZone || '').trim());
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export interface ResolvedTripTimeZone {
  timeZone: string;
  /** True when the group has no usable `time_zone` and we fell back to the browser's. */
  isFallback: boolean;
}

/**
 * The zone all of a group's times are entered and displayed in. A group with no
 * `time_zone` (or a corrupt one) falls back to the browser's zone, flagged so the
 * UI can say so out loud rather than quietly guessing on the host's behalf.
 */
export function resolveTripTimeZone(groupTimeZone?: string | null): ResolvedTripTimeZone {
  const candidate = (groupTimeZone || '').trim();
  if (candidate && isUsableZone(candidate)) return { timeZone: candidate, isFallback: false };
  return { timeZone: browserTimeZone(), isFallback: true };
}

/** UTC-ms minus zone-local-ms at a given instant, i.e. the zone's offset in ms. */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instantMs));

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asIfUtc = Date.UTC(
    get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'),
  );
  // Intl drops sub-second precision; ignore it on both sides so the difference is
  // a clean offset rather than offset-plus-milliseconds.
  return asIfUtc - Math.floor(instantMs / 1000) * 1000;
}

function offsetSuffix(offsetMs: number): string {
  const sign = offsetMs < 0 ? '-' : '+';
  const total = Math.abs(Math.round(offsetMs / 60000));
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

/**
 * "2026-08-15T17:30" + "Europe/Lisbon" -> "2026-08-15T17:30:00+01:00".
 *
 * The offset is looked up at the resulting instant, not at "now", so a summer
 * event authored in winter still gets the summer offset. Returns null for empty
 * or unparseable input so callers can send `undefined` and leave the field unset.
 */
export function wallClockToIso(local: string, timeZone: string): string | null {
  const match = ISO_LOCAL.exec((local || '').trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const zone = isUsableZone(timeZone) ? timeZone : browserTimeZone();

  const wallMs = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s || '0'));
  if (Number.isNaN(wallMs)) return null;

  // First guess uses the offset in force at the same clock reading interpreted as
  // UTC; re-checking at the derived instant settles the DST-transition cases
  // where those two offsets differ.
  let offset = zoneOffsetMs(wallMs, zone);
  const refined = zoneOffsetMs(wallMs - offset, zone);
  if (refined !== offset) offset = refined;

  return `${y}-${mo}-${d}T${h}:${mi}:${s || '00'}${offsetSuffix(offset)}`;
}

/**
 * Render a stored instant back in the trip's zone, with the zone named. Times
 * that a host cannot see are times a host cannot catch a mistake in, so every
 * surface that accepts a time also prints one.
 */
export function formatInZone(
  iso: string | null | undefined,
  timeZone: string,
  fallback = 'Date TBD',
): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  const zone = isUsableZone(timeZone) ? timeZone : browserTimeZone();
  const stamp = new Intl.DateTimeFormat(undefined, {
    timeZone: zone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
  return `${stamp} · ${zone}`;
}

/**
 * A stored instant as a DATE only ("Fri, Oct 2, 2026"), read in the trip's zone. For a fact whose clock time nobody
 * needs (a confirmation went out that day): no time, no zone id to decode.
 */
export function formatDateInZone(
  iso: string | null | undefined,
  timeZone: string,
  fallback = 'Date TBD',
): string {
  if (!iso) return fallback;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return fallback;
  const zone = isUsableZone(timeZone) ? timeZone : browserTimeZone();
  return new Intl.DateTimeFormat(undefined, {
    timeZone: zone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(date);
}

/**
 * Intl formatters, kept rather than rebuilt.
 *
 * `new Intl.DateTimeFormat` is the most expensive call in the Intl API, and the
 * itinerary tab rebuilds its whole card grid on every keystroke — a 40-tile
 * weekend was constructing hundreds of them per character typed. Keyed by zone
 * and shape; the map is bounded by the number of zones a trip actually uses.
 */
const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string, shape: 'stamp' | 'clock' | 'dayClock' | 'isoDay'): Intl.DateTimeFormat {
  const key = `${zone}|${shape}`;
  const cached = FORMATTERS.get(key);
  if (cached) return cached;
  const options: Record<typeof shape, Intl.DateTimeFormatOptions> = {
    stamp: { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
    clock: { timeZone: zone, hour: 'numeric', minute: '2-digit' },
    dayClock: { timeZone: zone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
    // en-CA gives YYYY-MM-DD, which is only ever compared, never shown.
    isoDay: { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' },
  };
  const made = shape === 'isoDay'
    ? new Intl.DateTimeFormat('en-CA', options.isoDay)
    : new Intl.DateTimeFormat(undefined, options[shape]);
  FORMATTERS.set(key, made);
  return made;
}

/**
 * A start and an end as one line, with the zone named ONCE, at the end.
 *
 * The naive composition — `formatInZone(start) + ' – ' + endClock(end)` — puts
 * the zone inside the range: "7:30 PM · Europe/Lisbon – 10:00 PM", which reads
 * as running from a place to a time. The zone suffix has to be appended to the
 * whole thing, which is only possible if one function owns both halves.
 *
 * The end drops its date when the event finishes on the same day IN THE TRIP'S
 * ZONE. That qualifier is the point: Lisbon is UTC+1 in August, so 00:30 local
 * on the 16th is 23:30 UTC on the 15th — comparing the instants' UTC dates
 * calls that same-day and prints a finish nineteen hours before the start.
 *
 * Falls back to the start alone whenever the end cannot be trusted, so a caller
 * never has to guard against a half-formed range or a dangling dash.
 */
export function formatRangeInZone(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timeZone: string,
  fallback = 'Date TBD',
): string {
  if (!startIso) return fallback;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return fallback;
  const zone = isUsableZone(timeZone) ? timeZone : browserTimeZone();

  const head = formatter(zone, 'stamp').format(start);
  const end = endIso ? new Date(endIso) : null;
  // An end at or before the start is bad data — a row written before the
  // server validated the pair. Showing it as a range presents it as a fact.
  if (!end || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
    return `${head} · ${zone}`;
  }

  const sameDay = formatter(zone, 'isoDay').format(start) === formatter(zone, 'isoDay').format(end);
  const tail = formatter(zone, sameDay ? 'clock' : 'dayClock').format(end);
  return `${head} – ${tail} · ${zone}`;
}

/**
 * The inverse of {@link wallClockToIso}: a stored instant back to the
 * "YYYY-MM-DDTHH:mm" a datetime-local input wants, read in the trip's zone.
 *
 * Needed to load an existing item into the edit form. Doing this with the
 * browser's own toISOString().slice(0,16) would render the time in the editor's
 * zone, so a host in Chicago opening a Lisbon dinner would see 12:30, and simply
 * pressing Save — changing nothing — would move the event six hours. Round-trips
 * exactly with wallClockToIso, DST included.
 */
export function isoToWallClock(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const zone = isUsableZone(timeZone) ? timeZone : browserTimeZone();

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  const day = `${get('year')}-${get('month')}-${get('day')}`;
  if (!get('year') || !hour) return '';
  return `${day}T${hour}:${get('minute')}`;
}
